// The two shapes the API exposes for the Bol demonstration.
//
// `Contact` is what the application speaks. `ExternalContact` is what the upstream
// service returned, before Ebi.Common.Bol translated it -- exposed only so the
// difference can be shown side by side on screen. A real API would not publish the
// upstream shape at all.

export interface Contact {
  id: string
  fullName: string
  emailAddress: string
  companyName: string
  city: string
  importedUtc: string // ISO timestamp
}

export interface ExternalContact {
  sourceId: string
  name: string
  email: string
  company: string
  city: string
  retrievedUtc: string
}

// Empty base => relative URLs. Same reasoning as api/locations.ts: CloudFront serves
// /api/* from this origin in production, Vite proxies it in development.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

async function get<T>(path: string): Promise<T> {
  // credentials: 'include' because both endpoints are [Authorize] -- the auth cookie
  // has to travel with the request or the API answers 401.
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' })

  if (res.status === 401) {
    throw new Error('Not signed in. Sign in to load this data.')
  }
  if (!res.ok) {
    throw new Error(`${path} returned ${res.status}`)
  }

  return (await res.json()) as T
}

/** After Bol: the upstream data in this application's own vocabulary. */
export const listContacts = () => get<Contact[]>('/api/contacts')

/** Before Bol: exactly what the upstream service handed back. */
export const listRawContacts = () => get<ExternalContact[]>('/api/contacts/raw')
